import React, { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
    closeMobileSidebar,
    ensureSidebarOverlay,
    toggleMiniSidebar,
} from './layoutUtils'
import { getStoredUser, getUserDisplayName, signOut } from '../config/authUtils'

const Sidebar = () => {
    const location = useLocation()
    const navigate = useNavigate()

    const isActive = (path) => location.pathname === path

    useEffect(() => {
        const overlay = ensureSidebarOverlay()
        const handleOverlayClick = () => closeMobileSidebar()
        overlay.addEventListener('click', handleOverlayClick)
        return () => overlay.removeEventListener('click', handleOverlayClick)
    }, [])

    useEffect(() => {
        closeMobileSidebar()
    }, [location.pathname])

    const storedUser = getStoredUser()

    const handleLogout = () => {
        signOut()
        closeMobileSidebar()
        navigate('/lockscreen', { replace: true })
    }

    const navLinkClass = (path) => (isActive(path) ? 'active' : '')

    return (
        <div className="main-wrapper">
            <div className="sidebar shadow-sm" id="sidebar" style={{ borderRight: '1px solid rgba(0,0,0,0.05)', backgroundColor: 'var(--bs-body-bg)' }}>
                <div className="sidebar-logo border-bottom" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                    <div>
                        <Link to="/" className="logo logo-normal">
                            <img style={{ height: '60px', width: '70px', objectFit: 'contain' }} src="/assets/img/media/burray.png" alt="Logo" />
                        </Link>
                        <Link to="/" className="logo-small">
                            <img src="/assets/img/media/burray.png" alt="Logo" />
                        </Link>
                        <Link to="/" className="dark-logo">
                            <img src="/assets/img/media/burray.png" alt="Logo" />
                        </Link>
                    </div>
                    <button
                        type="button"
                        className="sidenav-toggle-btn btn p-0"
                        id="toggle_btn"
                        onClick={toggleMiniSidebar}
                        aria-label="Toggle sidebar"
                    >
                        <i className="ti ti-chevron-left-pipe" />
                    </button>
                    <button
                        type="button"
                        className="sidebar-close"
                        onClick={closeMobileSidebar}
                        aria-label="Close sidebar"
                    >
                        <i className="ti ti-x align-middle" />
                    </button>
                </div>

                <div 
                    className="sidebar-inner" 
                    style={{ 
                        overflowY: 'auto', 
                        height: 'calc(100vh - 60px)', 
                        display: 'flex', 
                        flexDirection: 'column' 
                    }}
                >
                    <div id="sidebar-menu" className="sidebar-menu flex-grow-1">
                        <ul className="pt-3">
                            <li className="menu-title"><span className="fw-semibold text-uppercase tracking-wider opacity-75">Main Menu</span></li>
                            <li>
                                <ul>
                                    <li>
                                        <Link to="/" className={navLinkClass('/')}>
                                            <i className="ti ti-layout-dashboard" /><span>Dashboard</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/products" className={navLinkClass('/products')}>
                                            <i className="ti ti-package" /><span>Products</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/clients" className={navLinkClass('/clients')}>
                                            <i className="ti ti-users" /><span>Clients</span>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            <li className="mt-2">
                                <ul>
                                    <li>
                                        <Link to="/quotations" className={navLinkClass('/quotations')}>
                                            <i className="ti ti-file-description" /><span>Quotations</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/invoices" className={navLinkClass('/invoices')}>
                                            <i className="ti ti-file-invoice" /><span>Invoices</span>
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="/invoices/recurring" className={navLinkClass('/invoices/recurring')}>
                                            <i className="ti ti-repeat" /><span>Recurring Invoices</span>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            
                            <li className="mt-2">
                                <ul>
                                    <li>
                                        <Link to="/reports" className={navLinkClass('/reports')}>
                                            <i className="ti ti-chart-pie" /><span>Reports</span>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                            
                            <li className="menu-title mt-4"><span className="fw-semibold text-uppercase tracking-wider opacity-75">Preferences</span></li>
                            <li>
                                <ul>
                                    <li>
                                        <Link to="/settings" className={navLinkClass('/settings')}>
                                            <i className="ti ti-settings" /><span>Settings</span>
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>

                    <div className="sidebar-footer mt-auto p-3 border-top" style={{ borderColor: 'rgba(0,0,0,0.05)', backgroundColor: 'var(--bs-body-bg)' }}>
                        <div className="p-2 mb-3 rounded-3 d-flex align-items-center" style={{ backgroundColor: 'var(--bs-light)', border: '1px solid rgba(0,0,0,0.05)', transition: 'all 0.3s ease' }}>
                            <span className="avatar avatar-md me-3 shadow-sm rounded-circle border border-white">
                                <img src="/assets/img/avatar101.jpg" alt="" className="rounded-circle" />
                            </span>
                            <div className="overflow-hidden">
                                <h6 className="fs-14 fw-bold mb-0 text-truncate">{getUserDisplayName(storedUser)}</h6>
                                <p className="fs-12 text-muted mb-0 text-truncate">{storedUser?.email || '—'}</p>
                            </div>
                        </div>
                        <button 
                            type="button" 
                            className="btn w-100 d-flex align-items-center justify-content-center fw-medium shadow-sm transition-all" 
                            onClick={handleLogout}
                            style={{ backgroundColor: '#fff0f0', color: '#dc3545', border: '1px solid #ffdcdc' }}
                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#dc3545'; e.currentTarget.style.color = '#fff' }}
                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff0f0'; e.currentTarget.style.color = '#dc3545' }}
                        >
                            <i className="ti ti-logout-2 me-2 fs-16" />Logout
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Sidebar

